import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted } from 'vue'
import { KeyCode } from '@/utils/enum'
import {
  getMeridianFrom24Hour,
  hours12To24Number,
  hours24To12Number,
} from '@/utils/timeStrings'
import { generateShortUUID } from '@/utils/id'

// Simple input masking function

export default /*#__PURE__*/_defineComponent({
  __name: 'WheelsDigitalTimePicker',
  props: {
    value: null
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: string | null): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: string | null
};

function applyMask(value: string, maxLength: number = 2): string {
  // Remove non-digits and limit length
  const digits = value.replace(/\D/g, '')
  if (digits.startsWith('0')) {
    return digits.slice(-maxLength)
  }
  return digits.slice(0, maxLength)
}
// Props


// Emits


// Reactive state
const isAM = ref(true)
const isPM = ref(false)
const hour = ref<string | null>(null)
const minute = ref<string | null>(null)
const uuid = ref('')

// Template refs
const hourInputRef = ref<HTMLInputElement>()
const minuteInputRef = ref<HTMLInputElement>()

// Computed properties
const hours24 = computed((): number | null => {
  if (hour.value === null) {
    return null
  }
  return hours12To24Number(parseInt(hour.value), isAM.value ? 'AM' : 'PM')
})

const isoTime = computed((): string | null => {
  if (
    (hours24.value === null && hours24.value !== 0) ||
    (!minute.value && parseInt(minute.value || '') !== 0)
  ) {
    return null
  }
  const hourStr = hours24.value! < 10 ? `0${hours24.value}` : `${hours24.value}`
  const minuteStr = minute.value || '00'
  return `${hourStr}:${minuteStr}:00`
})

// Watchers
watch(isoTime, (value) => {
  emit('input', value)
})

watch(
  () => props.value,
  (value) => {
    updateInputsFromValueChange(value)
  },
)

// Lifecycle
onMounted(() => {
  uuid.value = generateShortUUID()
  if (props.value) {
    updateInputsFromValueChange(props.value)
  }
})

// Methods
function setToAM(): void {
  isAM.value = true
  isPM.value = false
}

function setToPM(): void {
  isPM.value = true
  isAM.value = false
}

function toggleMeridian(): void {
  if (isAM.value) {
    setToPM()
  } else {
    setToAM()
  }
}

function hourKeyPressHandler(event: KeyboardEvent): void {
  const keyCode = event.keyCode

  if (keyCode === KeyCode.Colon) {
    event.preventDefault()
    minuteInputRef.value?.focus()
    return
  }

  if (keyCode === KeyCode.ArrowUp) {
    event.preventDefault()
    const current = parseInt(hour.value || '12')
    setHourValue(current + 1)
    return
  }

  if (keyCode === KeyCode.ArrowDown) {
    event.preventDefault()
    const current = parseInt(hour.value || '1')
    setHourValue(current - 1)
    return
  }

}

function minuteKeyPressHandler(event: KeyboardEvent): void {
  const keyCode = event.keyCode

  if (keyCode === KeyCode.ArrowUp) {
    event.preventDefault()
    const current = parseInt(minute.value || '59')
    setMinuteValue(current + 1)
    return
  }

  if (keyCode === KeyCode.ArrowDown) {
    event.preventDefault()
    const current = parseInt(minute.value || '0')
    setMinuteValue(current - 1)
    return
  }

}

function handleHourInput(event: Event): void {
  const target = event.target as HTMLInputElement
  const value = target.value

  // Apply masking first (digits only, max 2 chars)
  const maskedValue = applyMask(value, 2)

  // If masking changed the value, update the input
  if (maskedValue !== value) {
    target.value = maskedValue
  }

  if (maskedValue === '') {
    hour.value = null
    target.value = ''
    return
  }

  const numericValue = parseInt(maskedValue)
  if (isNaN(numericValue) || numericValue < 1 || numericValue > 12) {
    hour.value = null
    target.value = ''
    return
  }

  hour.value = String(numericValue)
  target.value = hour.value
}

function handleMinuteInput(event: Event): void {
  const target = event.target as HTMLInputElement
  const value = target.value

  // Apply masking first (digits only, max 2 chars)
  const maskedValue = applyMask(value, 2)

  // If masking changed the value, update the input
  if (maskedValue !== value) {
    target.value = maskedValue
  }

  // Parse and validate range for direct input
  if (maskedValue === '') {
    minute.value = null
    target.value = ''
    return
  }

  const numericValue = parseInt(maskedValue)
  if (isNaN(numericValue) || numericValue < 0 || numericValue > 59) {
    // Invalid value - clear the input
    minute.value = null
    target.value = ''
    return
  }

  // Valid value - set it with proper formatting
  const formattedValue = numericValue < 10 ? `0${numericValue}` : String(numericValue)
  minute.value = formattedValue
  target.value = formattedValue
}

function setHourValue(valueToSet: number | null): void {
  const element = hourInputRef.value
  const value = valueToSet !== null ? valueToSet : element?.value

  if (value === '' || value === null || value === undefined) {
    hour.value = null
    return
  }

  const intValue = parseInt(String(value))
  if (isNaN(intValue)) {
    if (element) element.value = hour.value || ''
    return
  }

  if (String(value).length > 2) {
    return
  }

  let finalValue = intValue

  // Handle wrapping and AM/PM toggle
  if (intValue > 12) {
    finalValue = 1
    toggleMeridian()
  } else if (intValue < 1) {
    finalValue = 12
    toggleMeridian()
  }

  hour.value = String(finalValue)
  if (element) element.value = hour.value
}

function setMinuteValue(valueToSet: number | null): void {
  const element = minuteInputRef.value
  const value = valueToSet !== null ? valueToSet : element?.value

  if (value === '' || value === null || value === undefined) {
    minute.value = null
    return
  }

  const intValue = parseInt(String(value))
  if (isNaN(intValue)) {
    if (element) element.value = minute.value || ''
    return
  }

  let finalValue = intValue

  // Handle wrapping and hour adjustment
  if (intValue > 59) {
    finalValue = 0
    const currentHour = parseInt(hour.value || '12')
    setHourValue(currentHour + 1)
  } else if (intValue < 0) {
    finalValue = 59
    const currentHour = parseInt(hour.value || '1')
    setHourValue(currentHour - 1)
  }

  const formattedValue = finalValue < 10 ? `0${finalValue}` : String(finalValue)

  minute.value = formattedValue
  if (element) element.value = formattedValue
}

function meridianKeyPressHandler(event: KeyboardEvent): void {
  const keyCode = event.keyCode

  // Don't update AM/PM if modifier keys are pressed (Cmd, Ctrl, Alt, Shift)
  // Prevents updating the time if we're selecting an input with Ctrl+A or similar
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
    return
  }

  if (keyCode === KeyCode.A) {
    setToAM()
    return
  }

  if (keyCode === KeyCode.P) {
    setToPM()
    return
  }
}

function updateInputsFromValueChange(value: string | null): void {
  if (!value) return

  const splitValue = value.split(':')
  const newHour = parseInt(splitValue[0])
  const newHour12Hour = hours24To12Number(newHour)
  const newMinute = parseInt(splitValue[1])
  const oldHour = parseInt(hour.value || '0')
  const oldMinute = minute.value === null ? null : parseInt(minute.value)

  if (newHour12Hour !== oldHour) {
    setHourValue(newHour12Hour)
    if (getMeridianFrom24Hour(newHour) === 'AM') {
      setToAM()
    } else {
      setToPM()
    }
  }

  if (minute.value === null || newMinute !== oldMinute) {
    setMinuteValue(newMinute)
  }
}

return { __sfc: true,applyMask, props, emit, isAM, isPM, hour, minute, uuid, hourInputRef, minuteInputRef, hours24, isoTime, setToAM, setToPM, toggleMeridian, hourKeyPressHandler, minuteKeyPressHandler, handleHourInput, handleMinuteInput, setHourValue, setMinuteValue, meridianKeyPressHandler, updateInputsFromValueChange }
}

})